import { useEffect, useRef, useState } from 'react';

import { getTermsAcceptance, getTermsDocuments, type LegalDocumentText, type TermsAcceptanceState } from '../../api/legal';
import { documentLabel, LegalDocumentsBox, TermsAcceptancePanel } from '../TermsAcceptancePanel';
import { useTermsGate } from '../TermsGate';

const SURFACE_LABELS: Record<string, string> = {
  app: 'In the app', checkout: 'At checkout', operator: 'Recorded by Auteur support from your written acceptance',
};

function when(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/** Settings > Legal: what this person accepted and when, with links to the documents. Read-only history. */
export function LegalPanel() {
  const gate = useTermsGate();
  const [state, setState] = useState<TermsAcceptanceState | null>(null);
  const [texts, setTexts] = useState<LegalDocumentText[] | null>(null);
  const [error, setError] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const live = useRef(false);

  async function load() {
    setError('');
    // The full text is a reading convenience: without it the documents are offered as links.
    getTermsDocuments().then((loaded) => { if (live.current) setTexts(loaded.documents); }).catch(() => {});
    try {
      const current = await getTermsAcceptance();
      if (live.current) setState(current);
    } catch {
      if (live.current) setError('Your terms acceptance could not be loaded. Please try again.');
    }
  }

  useEffect(() => {
    live.current = true;
    void load();
    return () => { live.current = false; };
  }, []);

  return (
    <section className="settings-panel-content" aria-labelledby="settings-legal-title">
      <h3 id="settings-legal-title">Legal</h3>
      {!state && !error ? <p role="status">Loading your terms acceptance…</p> : null}
      {error ? <><p role="alert">{error}</p>
        <button type="button" className="secondary" onClick={() => void load()}>Retry</button></> : null}
      {state ? <>
        <p>Current terms version: <strong>{state.currentVersion}</strong></p>
        {state.accepted
          ? <p role="status">You accepted version {state.currentVersion}{state.acceptedAt ? ` on ${when(state.acceptedAt)}` : ''}.</p>
          : <p role="status">
            {state.acceptedVersion
              ? `You accepted version ${state.acceptedVersion}; version ${state.currentVersion} needs your acceptance.`
              : 'You have not accepted the current terms yet.'}
            {state.required ? ' Creating a workspace and paid generation stay unavailable until you accept.' : ''}
          </p>}
        <h4>Documents</h4>
        {texts && texts.length ? <LegalDocumentsBox documents={texts} label="Legal documents" /> : <ul className="settings-legal-documents">
          {state.documents.map((doc) => <li key={doc.id}>
            <a href={doc.url} target="_blank" rel="noopener noreferrer">{documentLabel(doc.id)} (opens in a new tab)</a>
          </li>)}
          <li><a href="https://auteurintelligence.com/legal/refunds/" target="_blank" rel="noopener noreferrer">Refund and Cancellation Policy (opens in a new tab)</a></li>
          <li><a href="https://auteurintelligence.com/legal/subprocessors/" target="_blank" rel="noopener noreferrer">Service Providers and Subprocessors (opens in a new tab)</a></li>
        </ul>}
        {state.documents.some((doc) => doc.sha256) ? <p className="settings-account-note">Fingerprints of the signed documents:{' '}
          {state.documents.filter((doc) => doc.sha256).map((doc, index) => <span key={doc.id} title={doc.sha256}>
            {index ? ' · ' : ''}{documentLabel(doc.id)} {doc.sha256!.slice(0, 12)}</span>)}</p> : null}
        <h4>Your acceptance history</h4>
        {state.history.length ? <table className="settings-legal-history">
          <thead><tr><th scope="col">Version</th><th scope="col">Accepted</th><th scope="col">Where</th></tr></thead>
          <tbody>{state.history.map((row) => <tr key={`${row.termsVersion}-${row.acceptedAt}`}>
            <td>{row.termsVersion}</td><td>{when(row.acceptedAt)}</td><td>{SURFACE_LABELS[row.surface] || row.surface || '—'}</td>
          </tr>)}</tbody>
        </table> : <p>No acceptance recorded yet.</p>}
        <p className="settings-account-note">Acceptance records are kept permanently and cannot be edited or deleted.</p>
        {!state.accepted && !reviewing
          ? <button type="button" onClick={() => setReviewing(true)}>Review and accept the current terms</button> : null}
        {!state.accepted && reviewing ? <TermsAcceptancePanel mode="gate" onContinue={(confirmed) => {
          setReviewing(false);
          if (confirmed) { setState(confirmed); gate?.markAccepted(confirmed); }
        }} /> : null}
      </> : null}
    </section>
  );
}
